import React from 'react';
import { DateTime } from 'luxon';
import styled from 'styled-components';
import { filterBoolean, formatCurrency, formatNumber, roundNumber, uniqueArray } from '/imports/utils';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { SolarBill, solarBillAmountDue, solarBillSolarCharges } from '/imports/api/solarBills';

export const RealtyIncomeBillStyled = styled.div`
  @media screen, print {
    font-family: 'Inter', sans-serif;
    width: 8.5in;
    position: absolute;
    top: 0;
    left: 0;
    .sheet {
      width: 8.5in;
      height: 11in;
      border: 1px solid black;
      box-sizing: border-box;
      overflow: hidden;
      position: relative;
      color: rgb(62, 72, 78);
      div, img, h2 {
        padding: 0;
        margin: 0;
        position: absolute;
      }
    }
  }
`;

/**
 * Resolve a tenant logo image path from an org sid, defaulting to the house logo.
 * @param sid - org short id
 * @returns image src path
 */
const getLogo = (sid?: string) => {
  if (sid === 'dollarGeneral') return '/images/realtyIncome/dollarGeneral.png';
  if (sid === 'tractorSupply') return '/images/realtyIncome/tractorSupply.png';
  if (sid === 'ultaBeauty') return '/images/realtyIncome/ultaBeauty.png';
  return '/images/logo_dark_193_79.png';
};

const alternateRemittanceAddressMeterIds = [
  'JstSF44M8n8N9jrTW', // Hanford
  'EN8ofB29LdaoMBQBW', // Riverside
  'g9um9QS5PjjoutPoH', // Murietta
];

/** Print-ready Realty Income solar bill sheet. */
export const RealtyIncomeBill = (props: { bill: SolarBill }) => {
  const bill = props.bill;
  const meters = bill._meters;
  const useAlternateRemittance = meters.reduce((r, m) => r || alternateRemittanceAddressMeterIds.includes(m._id), false);
  const [issuedYear, issuedMonth, issuedDay] = bill.issuedDate.split('-');
  const issuedDate = `${issuedMonth}/${issuedDay}/${issuedYear}`;
  const [dueYear, dueMonth, dueDay] = bill.dueDate.split('-');
  const dueDate = `${dueMonth}/${dueDay}/${dueYear}`;
  const [startYear, startMonth, startDay] = bill.startDate.split('-');
  const startDate = `${startMonth}/${startDay}/${startYear}`;
  const [endYear, endMonth, endDay] = bill.endDate.split('-');
  const endDate = `${endMonth}/${endDay}/${endYear}`;
  const daysInPeriod = DateTime.fromISO(bill.endDate).diff(DateTime.fromISO(bill.startDate), 'days').days + 1;
  const logo = getLogo(bill.extra?.orgSid);
  return (
    <RealtyIncomeBillStyled className="bill northshore-bill">
      <div className="sheet">
        <div style={{ top: '0', left: '0', width: '100%', height: '4cm' }}>
          <img style={{ top: '0.9cm', left: '0.75cm', height: '1.75cm' }} src={logo} alt="Realty Income" />
          <div style={{ top: '0.5cm', left: '5.25cm', height: '2.5cm', borderRight: '1px solid black' }} />
          <div style={{ top: '1cm', left: '5.75cm', height: '1.8cm', width: '6cm', fontSize: '0.4cm' }}>
            <span>11995 El Camino Real</span><br />
            <span>San Diego, California 92130</span><br />
            <span>www.realtyincome.com</span>
          </div>
          <div style={{ top: '0.75cm', right: '0.75cm ' }}>Solar electricity powered by</div>
          <img style={{ top: '1cm', right: '0.3cm', height: '1.75cm' }} src="/images/logo_dark.png" />
          <div style={{ top: '3.5cm', left: '0.75cm', right: '0.75cm', width: 'calc(100% - 1.5cm)', borderBottom: '1px solid #9cbfea' }} />
        </div>
        <div style={{ top: '3.5cm', left: '0.75cm' }}>
          <div style={{ top: '0.75cm', width: '5cm', fontWeight: 'bold' }}>DATE:</div><div style={{ top: '0.75cm', left: '4.75cm', width: '7.5cm' }}>{issuedDate}</div>
          <div style={{ top: '1.75cm', width: '5cm', fontWeight: 'bold' }}>ACCOUNT NUMBER:</div><div style={{ top: '1.75cm', left: '4.75cm', width: '7.5cm' }}>{bill.accountNumber}</div>
          <div style={{ top: '2.75cm', width: '5cm', fontWeight: 'bold' }}>ACCOUNT HOLDER:</div><div style={{ top: '2.75cm', left: '4.75cm', width: '7.5cm' }}>{bill.customerName || bill.accountNumber}</div>
          <div style={{ top: '3.75cm', width: '5cm', fontWeight: 'bold' }}>SERVICE ADDRESS:</div><div style={{ top: '3.75cm', left: '4.75cm', width: '5.5cm' }}>{uniqueArray(filterBoolean(bill._meters.map((m) => m.address?.formatted))).join(' | ')}</div>
        </div>
        <div style={{ top: '9cm', left: '0.75cm' }}>
          <h2 style={{ top: '0', left: '0', width: '8cm', fontWeight: 'bold', color: '#407aaf' }}>Account Summary</h2>
          <div style={{ top: '1cm', left: '0', background: '#f4f5f6', width: '12.25cm', height: '1.2cm', fontSize: '0.325cm', fontWeight: 'bold' }}>
            <div style={{ top: '1mm', left: '1mm' }}>Current Charges</div>
            <div style={{ top: '1mm', left: '3.5cm' }}>{startDate} - {endDate}</div>
            <div style={{ top: '1mm', right: '1mm', width: '3cm', textAlign: 'right', paddingRight: '1mm', boxSizing: 'border-box', border: '1px solid black' }}>{formatCurrency(solarBillSolarCharges(bill))}</div>
            <div style={{ top: '0.75cm', left: '3.5cm' }}>Solar Production: <span style={{ fontWeight: 'normal' }}>{formatNumber(bill.production || 0)} kWh</span></div>
            <div style={{ top: '0.75cm', right: '1mm' }}>PPA Rate: <span style={{ fontWeight: 'normal' }}>${roundNumber(bill.ppaRate || 0, 4)}</span></div>
          </div>
          {
            bill.previousCharges ?
              <div style={{ top: '3cm', left: '0', background: '#f4f5f6', width: '12.25cm', height: '0.625cm', fontSize: '0.325cm', fontWeight: 'bold' }}>
                <div style={{ top: '1mm', left: '1mm' }}>Previous Balance</div>
                <div style={{ top: '1mm', right: '1mm', width: '3cm', textAlign: 'right', paddingRight: '1mm', boxSizing: 'border-box', border: '1px solid black' }}>{formatCurrency(bill.previousCharges)}</div>
              </div>
              :
              null
          }
          {
            bill.lateCharges ?
              <div style={{ top: '4.5cm', left: '0', background: '#f4f5f6', width: '12.25cm', height: '0.625cm', fontSize: '0.325cm', fontWeight: 'bold' }}>
                <div style={{ top: '1mm', left: '1mm' }}>Late Fees</div>
                <div style={{ top: '1mm', right: '1mm', width: '3cm', textAlign: 'right', paddingRight: '1mm', boxSizing: 'border-box', border: '1px solid black' }}>{formatCurrency(bill.lateCharges)}</div>
              </div>
              :
              null
          }
          {
            bill.credits ?
              <div style={{ top: '6cm', left: '0', background: '#f4f5f6', width: '12.25cm', height: '0.625cm', fontSize: '0.325cm', fontWeight: 'bold' }}>
                <div style={{ top: '1mm', left: '1mm' }}>Credit</div>
                <div style={{ top: '1mm', right: '1mm', width: '3cm', textAlign: 'right', paddingRight: '1mm', boxSizing: 'border-box', border: '1px solid black' }}>{formatCurrency(bill.credits)}</div>
              </div>
              :
              null
          }
        </div>
        <div style={{ top: '16.5cm', left: '0.75cm', width: '12.25cm', fontSize: '0.325cm' }}>
          <p><b>Banking Details</b><br />ACH details provided upon request.</p>
          <p><b>Payment Terms.</b><br />All amounts are due and payable net thirty (30) days following receipt of invoice. Customer shall make payment to Realty Income Corporation by ACH payment. Any undisputed portion of the invoice amount not paid within such thirty (30) day period shall accrue interest at the annual rate of two and one-half percent (2.5%) above the Prime Rate* (but not exceed the maximum rate permitted by law). All payments shall be made in U.S. dollars.</p>
          <p>*&quot;Prime Rate&quot; shall mean the annual prime rate of interest published in the Wall Street Journal for the applicable period during which interest is incurred pursuant to the terms of the Agreement.</p>
        </div>
        <div style={{ top: '23cm', left: '0.75cm', width: 'calc(100% - 1.5cm)', height: '4cm', fontSize: '0.4cm' }}>
          <div style={{ position: 'relative' }}>Mail Payment To:</div>
          <div style={{ position: 'relative', marginTop: '0.2cm', width: '100%', borderBottom: '2px dashed black' }} />
          <svg style={{ position: 'absolute', left: '50%', marginLeft: '-1.25rem', marginTop: '-1.8rem', background: 'white', padding: '1rem' }} xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#000000"><path d="M760-120 480-400l-94 94q8 15 11 32t3 34q0 66-47 113T240-80q-66 0-113-47T80-240q0-66 47-113t113-47q17 0 34 3t32 11l94-94-94-94q-15 8-32 11t-34 3q-66 0-113-47T80-720q0-66 47-113t113-47q66 0 113 47t47 113q0 17-3 34t-11 32l494 494v40H760ZM600-520l-80-80 240-240h120v40L600-520ZM240-640q33 0 56.5-23.5T320-720q0-33-23.5-56.5T240-800q-33 0-56.5 23.5T160-720q0 33 23.5 56.5T240-640Zm240 180q8 0 14-6t6-14q0-8-6-14t-14-6q-8 0-14 6t-6 14q0 8 6 14t14 6ZM240-160q33 0 56.5-23.5T320-240q0-33-23.5-56.5T240-320q-33 0-56.5 23.5T160-240q0 33 23.5 56.5T240-160Z"></path></svg>
          <div style={{ position: 'relative', top: '1cm' }}>
            {
              useAlternateRemittance ?
                <>
                  <span>Realty Income US Core Plus 3, LP</span><br />
                  <span>PO Box 104383</span><br />
                  <span>Pasadena, CA 91189-4383</span>
                </>
                :
                <>
                  <span>Realty Income Corporation</span><br />
                  <span>PO Box 842428</span><br />
                  <span>Los Angeles, CA 90084</span>
                </>
            }
          </div>
        </div>
        <div style={{ top: '4cm', right: '8.25cm', height: '18.5cm', borderRight: '2px solid #9cbfea' }} />
        <div style={{ top: '4cm', right: '0.75cm', width: '7cm', height: '2.25cm', border: '3px solid #9cbfea' }}></div>
        <div style={{ top: '5.125cm', right: '0.75cm', width: '7.15cm', height: '0', borderBottom: '3px solid #9cbfea' }}></div>
        <div style={{ top: '4.35cm', right: '5.5cm', fontWeight: 'bold' }}>DATE DUE</div>
        <div style={{ top: '4.35cm', right: '1cm', fontWeight: 'bold' }}>{dueDate}</div>
        <div style={{ top: '5.5cm', right: '4.675cm', fontWeight: 'bold' }}>AMOUNT DUE</div>
        <div style={{ top: '5.5cm', right: '1cm', fontWeight: 'bold' }}>{formatCurrency(solarBillAmountDue(bill))}</div>
        <h2 style={{ top: '7cm', right: '2.15cm', color: '#407aaf', fontSize: '0.5cm', fontWeight: 'bold' }}>Solar Production (kWh)</h2>
        <div style={{ top: '7.75cm', right: '0.75cm', width: '7.4cm', height: '5cm' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              margin={{ top: 5, left: -25, right: 5, bottom: 0 }}
              data={bill.historicalProduction}
              barGap={0}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" tickFormatter={(item) => item.toUpperCase()} tick={{ fontSize: 8 }} interval={0} />
              <YAxis tick={{ fontSize: 8 }} />
              <Bar dataKey="production" name="Solar Production" fill="#22B14C" isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div style={{ top: '13cm', right: '0.75cm' }}>
          <table>
            <tr>
              <td style={{ fontWeight: 'bold', color: '#407aaf', textAlign: 'right' }}>{formatNumber(bill.production)} kWh</td>
              <td style={{ fontWeight: 'bold' }}>Solar Production</td>
            </tr>
            <tr>
              <td style={{ fontWeight: 'bold', color: '#407aaf', textAlign: 'right' }}>{daysInPeriod}</td>
              <td style={{ fontWeight: 'bold' }}>Days in Billing Period</td>
            </tr>
          </table>
        </div>
      </div>
    </RealtyIncomeBillStyled>
  );
};